import { apiFetch } from '../api/client';
import type { AuthResponse } from './types';

/**
 * Auth calls, which deliberately bypass the authenticated client wrapper.
 *
 * They must not go through the 401-refresh-retry path: a failed login retried via refresh
 * makes no sense, and a failed refresh retried via refresh is an infinite loop.
 */
export function login(email: string, password: string): Promise<AuthResponse> {
  return apiFetch<AuthResponse>('/api/v1/auth/login', {
    method: 'POST',
    body: { email, password },
  });
}

/**
 * Exchanges the httpOnly refresh cookie for a new token pair.
 *
 * Called on boot to recover a session the page reload dropped, and by the 401 handler.
 */
export function refresh(): Promise<AuthResponse> {
  return apiFetch<AuthResponse>('/api/v1/auth/refresh', { method: 'POST' });
}

export function logout(accessToken: string): Promise<void> {
  // apiFetch<undefined>, not <void>: these endpoints answer 204 with no body, and the
  // client returns undefined for that. `void` as a generic argument is also what
  // no-invalid-void-type objects to.
  return apiFetch<undefined>('/api/v1/auth/logout', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

/**
 * Changes the password.
 *
 * Every refresh token is revoked and the security stamp rotates, so the caller's access
 * token dies with this call and a fresh sign-in is required. Not optional — the API
 * enforces it.
 */
export function changePassword(
  accessToken: string,
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  return apiFetch<undefined>('/api/v1/auth/change-password', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: { currentPassword, newPassword },
  });
}
